import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Line, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart } from "recharts";
import { useAppDispatch, useAppSelector } from "../../redux";
import { getAgentAnalytics, clearAnalytics, AnalyticsTimeframe, AgentAnalyticsTimePoint } from "../../redux/reducers/agentAnalyticsSlice";
import { Button, View, Text, Card, DateInput, Switch, Tooltip as UITooltip } from "../base";
import { Icon } from "../base/Icon";
import { DropdownField } from "../base/DropdownField";
import { COLORS } from "../../utils/chartData";

const formatMs = (ms: number): string => {
  if (!ms || ms === 0) return "0ms";
  return `${Math.round(ms)}ms`;
};

const formatPercentage = (value: number): string => {
  return `${(value || 0).toFixed(1)}%`;
};

const formatMinutes = (minutes: number): string => {
  if (!minutes || minutes === 0) return "0m";
  if (minutes < 1) return "< 1m";
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.floor(minutes % 60);
  if (hours === 0) return `${remainingMinutes}m`;
  if (remainingMinutes === 0) return `${hours}h`;
  return `${hours}h ${remainingMinutes}m`;
};

const formatTimestamp = (timestamp: string, timeframe?: AnalyticsTimeframe, includeDate: boolean = false, timezone?: string): string => {
  const date = new Date(timestamp);

  // Determine bucket type based on timeframe
  const minuteTimeframes: AnalyticsTimeframe[] = ["15m", "30m"];
  const hourlyTimeframes: AnalyticsTimeframe[] = ["1h", "3h", "6h", "12h", "24h"];

  const isMinuteBucket = minuteTimeframes.includes(timeframe || "30d");
  const isHourlyBucket = hourlyTimeframes.includes(timeframe || "30d");

  if (isMinuteBucket) {
    // Show time with minutes for minute buckets in specified timezone
    if (includeDate) {
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: timezone,
      });
    }

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    });
  } else if (isHourlyBucket) {
    // Show time for hourly buckets in specified timezone
    if (includeDate) {
      return date.toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: timezone,
      });
    }

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: timezone,
    });
  } else {
    // For daily buckets, the API groups by the specified timezone, returning dates like "2025-11-07"
    // Extract the date string and display as-is (no timezone conversion needed)
    const dateStr = timestamp.substring(0, 10); // "2025-11-07"
    const [year, month, day] = dateStr.split("-").map(Number);
    const displayDate = new Date(year, month - 1, day);

    return displayDate.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }
};

const formatTimeAxis = (timestamp: string, timeframe?: AnalyticsTimeframe, timezone?: string): string => {
  return formatTimestamp(timestamp, timeframe, false, timezone);
};

const timeframeOptions: { key: AnalyticsTimeframe; label: string }[] = [
  { key: "15m", label: "Last 15m" },
  { key: "30m", label: "Last 30m" },
  { key: "1h", label: "Last 1h" },
  { key: "3h", label: "Last 3h" },
  { key: "6h", label: "Last 6h" },
  { key: "12h", label: "Last 12h" },
  { key: "24h", label: "Last 24h" },
  { key: "3d", label: "Last 3d" },
  { key: "7d", label: "Last 7d" },
  { key: "30d", label: "Last 30d" },
];

const CustomTooltip = ({ active, payload, label, timeframe, timezone }: any) => {
  if (active && payload && payload.length) {
    // Format the timestamp label using the shared utility
    const formattedLabel = (() => {
      try {
        const date = new Date(label);
        if (isNaN(date.getTime())) return label;

        return formatTimestamp(label, timeframe, true, timezone);
      } catch {
        return label;
      }
    })();

    // Get the data point to access P5/P95 values
    const dataPoint = payload[0]?.payload;

    return (
      <View className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <Text className="font-medium mb-2">{formattedLabel}</Text>
        {payload.map((entry: any, index: number) => {
          // Skip the band entries, we'll show P5/P95 separately
          if (entry.name?.includes("Range")) return null;

          const formatValue = (value: any) => {
            if (typeof value === "number") {
              if (entry.name.includes("(ms)")) {
                return formatMs(value);
              } else if (entry.name.includes("(%)")) {
                return formatPercentage(value);
              } else if (entry.name.includes("(minutes)")) {
                return formatMinutes(value);
              }
              return value.toLocaleString();
            }
            return String(value);
          };

          return (
            <View key={index}>
              <View className="flex items-center space-x-2">
                <View
                  className="w-3 h-3 rounded"
                  style={{ backgroundColor: entry.color }}
                />
                <Text className="text-sm">
                  {entry.name}: {formatValue(entry.value)}
                </Text>
              </View>

              {/* Show P50/P95 for session length */}
              {entry.name === "Avg Session Length (minutes)" && dataPoint?.sessionLengthP50 !== undefined && (
                <View className="ml-5 mt-1">
                  <Text className="text-xs text-gray-600 dark:text-gray-400">
                    P50: {dataPoint.sessionLengthP50.toFixed(2)}m | P95: {dataPoint.sessionLengthP95.toFixed(2)}m
                  </Text>
                </View>
              )}

              {/* Show P50/P95 for TTS latency */}
              {entry.name === "Avg First TTS Latency (ms)" && dataPoint?.ttsLatencyBand && (
                <View className="ml-5 mt-1">
                  <Text className="text-xs text-gray-600 dark:text-gray-400">
                    P50: {formatMs(dataPoint.ttsLatencyBand[0])} | P95: {formatMs(dataPoint.ttsLatencyBand[1])}
                  </Text>
                </View>
              )}

              {/* Show P50/P95 for STT latency */}
              {entry.name === "Avg STT Latency (ms)" && dataPoint?.sttLatencyBand && (
                <View className="ml-5 mt-1">
                  <Text className="text-xs text-gray-600 dark:text-gray-400">
                    P50: {formatMs(dataPoint.sttLatencyBand[0])} | P95: {formatMs(dataPoint.sttLatencyBand[1])}
                  </Text>
                </View>
              )}

              {/* Show P50/P95 for turn around time */}
              {entry.name === "Avg Turn Around Time (ms)" && dataPoint?.turnAroundBand && (
                <View className="ml-5 mt-1">
                  <Text className="text-xs text-gray-600 dark:text-gray-400">
                    P50: {formatMs(dataPoint.turnAroundBand[0])} | P95: {formatMs(dataPoint.turnAroundBand[1])}
                  </Text>
                </View>
              )}

              {/* Show P50/P95 for turn depth */}
              {entry.name === "Avg Turn Depth" && dataPoint?.turnDepthBand && (
                <View className="ml-5 mt-1">
                  <Text className="text-xs text-gray-600 dark:text-gray-400">
                    P50: {dataPoint.turnDepthBand[0]?.toFixed(1) || "0.0"} | P95: {dataPoint.turnDepthBand[1]?.toFixed(1) || "0.0"}
                  </Text>
                </View>
              )}
            </View>
          );
        })}
      </View>
    );
  }
  return null;
};

export const AgentAnalytics = () => {
  const dispatch = useAppDispatch();
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentAnalytics, isLoading, error } = useAppSelector((state) => state.agentAnalytics);

  // Timeframes that use daily buckets and need timezone for grouping
  const dailyBucketTimeframes: AnalyticsTimeframe[] = ["3d", "7d", "30d", "custom"];
  const validTimeframes: AnalyticsTimeframe[] = ["15m", "30m", "1h", "3h", "6h", "12h", "24h", "3d", "7d", "30d", "custom"];

  // Get browser's timezone
  const getBrowserTimezone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

  // URL is the single source of truth - derive all values from searchParams
  const urlTimeframe = searchParams.get("timeframe") as AnalyticsTimeframe;
  const selectedTimeframe = validTimeframes.includes(urlTimeframe) ? urlTimeframe : "24h";
  const startDate = searchParams.get("startDate") || "";
  const endDate = searchParams.get("endDate") || "";
  const displayTimezone = searchParams.get("timezone") || getBrowserTimezone();

  // Local state only for UI elements not in URL
  const [showPercentileBands, setShowPercentileBands] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<"sessions" | "observability" | "user_experience">("sessions");

  // Temporary state for date inputs (before user clicks Apply)
  const [pendingStartDate, setPendingStartDate] = useState(startDate);
  const [pendingEndDate, setPendingEndDate] = useState(endDate);

  /**
   * Converts a local date string (YYYY-MM-DD) to UTC epoch milliseconds
   * representing the start or end of that day in the user's local timezone
   */
  const localDateToUtcEpoch = (dateStr: string, isEndOfDay: boolean = false): number => {
    const [year, month, day] = dateStr.split("-").map(Number);
    const localDate = new Date(year, month - 1, day);

    if (isEndOfDay) {
      localDate.setHours(23, 59, 59, 999);
    } else {
      localDate.setHours(0, 0, 0, 0);
    }

    return localDate.getTime();
  };

  // Update URL (which triggers data fetch via useEffect)
  const updateUrlParams = (timeframe: AnalyticsTimeframe, timezone: string, customStartDate?: string, customEndDate?: string) => {
    const newParams = new URLSearchParams();
    newParams.set("timeframe", timeframe);
    newParams.set("timezone", timezone);

    if (timeframe === "custom" && customStartDate && customEndDate) {
      newParams.set("startDate", customStartDate);
      newParams.set("endDate", customEndDate);
    }

    setSearchParams(newParams, { replace: true });
  };

  // Fetch data whenever URL params change
  useEffect(() => {
    if (!agentId) return;

    // Ensure URL has timezone (for shareability)
    if (!searchParams.get("timezone")) {
      updateUrlParams(selectedTimeframe, getBrowserTimezone(), startDate, endDate);
      return; // Let the URL update trigger another effect run
    }

    // Fetch data based on current URL params
    if (selectedTimeframe === "custom" && startDate && endDate) {
      const startEpoch = localDateToUtcEpoch(startDate, false);
      const endEpoch = localDateToUtcEpoch(endDate, true);

      dispatch(
        getAgentAnalytics({
          agentId,
          timeframe: "custom",
          startDate: startEpoch,
          endDate: endEpoch,
          timezone: displayTimezone,
        }),
      );
    } else if (selectedTimeframe !== "custom") {
      dispatch(
        getAgentAnalytics({
          agentId,
          timeframe: selectedTimeframe,
          ...(dailyBucketTimeframes.includes(selectedTimeframe) && { timezone: displayTimezone }),
        }),
      );
    }

    return () => {
      dispatch(clearAnalytics());
    };
  }, [dispatch, agentId, searchParams]);

  const handleTimeframeChange = (timeframe: AnalyticsTimeframe) => {
    // Clear pending date inputs when switching to predefined timeframes
    if (timeframe !== "custom") {
      setPendingStartDate("");
      setPendingEndDate("");
    }

    // Use browser timezone for new selections
    const timezone = getBrowserTimezone();

    if (timeframe === "custom") {
      // Don't update URL yet - wait for user to set dates and click Apply
      // Just update to show custom is selected (but keep old data)
      return;
    }

    updateUrlParams(timeframe, timezone);
  };

  const handleCustomDateRange = () => {
    if (pendingStartDate && pendingEndDate && agentId) {
      // Use browser timezone for new selections
      const timezone = getBrowserTimezone();
      updateUrlParams("custom", timezone, pendingStartDate, pendingEndDate);
    }
  };

  /**
   * Fills in missing time buckets with zero values across the entire time range
   * For daily buckets, rangeStart/rangeEnd should be local date strings (e.g., "2025-11-08")
   * For minute/hour buckets, rangeStart/rangeEnd should be ISO timestamps
   */
  const fillMissingBuckets = (
    timePoints: AgentAnalyticsTimePoint[] | undefined,
    bucketSize: "minute" | "hour" | "day",
    rangeStart?: string,
    rangeEnd?: string,
  ): AgentAnalyticsTimePoint[] => {
    const emptyPoint = (timestamp: string): AgentAnalyticsTimePoint => ({
      timestamp,
      sessions: { count: 0 },
      ttsLatency: { avg: 0, p50: 0, p95: 0 },
      sttLatency: { avg: 0, p50: 0, p95: 0 },
      firstResponseTime: { avg: 0, p50: 0, p95: 0 },
      turnAroundTime: { avg: 0, p50: 0, p95: 0 },
      sessionLength: { avg: 0, p50: 0, p95: 0 },
      errorRate: 0,
      transferRate: 0,
      turnDepth: { avg: 0, p50: 0, p95: 0 },
    });

    // If no time range provided, fall back to using data points range
    if (!rangeStart || !rangeEnd) {
      if (!timePoints || timePoints.length === 0) return [];
      const sorted = [...timePoints].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      rangeStart = sorted[0].timestamp;
      rangeEnd = sorted[sorted.length - 1].timestamp;
    }

    // Create a map of existing data points by their bucket key
    const dataMap = new Map<string, AgentAnalyticsTimePoint>();
    if (timePoints) {
      for (const point of timePoints) {
        const key = getBucketKey(point.timestamp, bucketSize);
        dataMap.set(key, point);
      }
    }

    const result: AgentAnalyticsTimePoint[] = [];

    if (bucketSize === "day") {
      // For daily buckets, iterate by local calendar days
      // rangeStart/rangeEnd are local date strings like "2025-11-08"
      const startDateStr = rangeStart.substring(0, 10);
      const endDateStr = rangeEnd.substring(0, 10);

      let [year, month, day] = startDateStr.split("-").map(Number);
      const [endYear, endMonth, endDay] = endDateStr.split("-").map(Number);
      const endDate = new Date(endYear, endMonth - 1, endDay);

      while (true) {
        const currentDate = new Date(year, month - 1, day);
        if (currentDate > endDate) break;

        const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        const existingPoint = dataMap.get(dateStr);

        if (existingPoint) {
          result.push(existingPoint);
        } else {
          result.push(emptyPoint(dateStr));
        }

        // Move to next day
        day++;
        const nextDate = new Date(year, month - 1, day);
        year = nextDate.getFullYear();
        month = nextDate.getMonth() + 1;
        day = nextDate.getDate();
      }
    } else {
      // For minute/hour buckets, use UTC timestamps
      const incrementMs = bucketSize === "minute" ? 60 * 1000 : 60 * 60 * 1000;

      let currentTime = truncateToBucket(new Date(rangeStart).getTime(), bucketSize);
      const endTime = new Date(rangeEnd).getTime();

      while (currentTime <= endTime) {
        const timestamp = new Date(currentTime).toISOString();
        const key = getBucketKey(timestamp, bucketSize);
        const existingPoint = dataMap.get(key);

        if (existingPoint) {
          result.push(existingPoint);
        } else {
          result.push(emptyPoint(timestamp));
        }

        currentTime += incrementMs;
      }
    }

    return result;
  };

  // Helper to get a consistent bucket key for a timestamp
  const getBucketKey = (timestamp: string, bucketSize: "minute" | "hour" | "day"): string => {
    if (bucketSize === "day") {
      // For daily buckets, API returns dates like "2025-11-07" (already local)
      return timestamp.substring(0, 10);
    } else if (bucketSize === "hour") {
      return `${timestamp.substring(0, 13)}:00`; // "2025-11-07T15:00"
    } else {
      return timestamp.substring(0, 16); // "2025-11-07T15:30"
    }
  };

  // Helper to truncate a timestamp to the start of its bucket (for minute/hour only)
  const truncateToBucket = (timeMs: number, bucketSize: "minute" | "hour"): number => {
    const date = new Date(timeMs);
    if (bucketSize === "hour") {
      date.setUTCMinutes(0, 0, 0);
    } else {
      date.setUTCSeconds(0, 0);
    }
    return date.getTime();
  };

  // Get bucket size for current timeframe
  const getBucketSize = (timeframe: AnalyticsTimeframe): "minute" | "hour" | "day" => {
    if (["15m", "30m"].includes(timeframe)) return "minute";
    if (["1h", "3h", "6h", "12h", "24h"].includes(timeframe)) return "hour";
    return "day";
  };

  // Prepare chart data from API time points with percentiles
  // Create timeframe and timezone-aware formatter
  const timeAxisFormatter = React.useCallback(
    (timestamp: string) => {
      return formatTimeAxis(timestamp, selectedTimeframe, displayTimezone);
    },
    [selectedTimeframe, displayTimezone],
  );

  const chartData = React.useMemo(() => {
    const bucketSize = getBucketSize(selectedTimeframe);

    // Determine date range for filling missing buckets
    // For daily buckets: use local dates to avoid timezone boundary issues
    // For minute/hour: use API's UTC timestamps
    let rangeStart = currentAnalytics?.startDate;
    let rangeEnd = currentAnalytics?.endDate;

    if (bucketSize === "day") {
      if (selectedTimeframe === "custom") {
        rangeStart = startDate;
        rangeEnd = endDate;
      } else {
        const daysMap: Record<string, number> = { "3d": 2, "7d": 6, "30d": 29 };
        const days = daysMap[selectedTimeframe] || 0;
        rangeEnd = new Date().toLocaleDateString("en-CA", { timeZone: displayTimezone });
        const [y, m, d] = rangeEnd.split("-").map(Number);
        const start = new Date(y, m - 1, d - days);
        rangeStart = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}-${String(start.getDate()).padStart(2, "0")}`;
      }
    }

    const timePoints = fillMissingBuckets(currentAnalytics?.timePoints, bucketSize, rangeStart, rangeEnd);

    return timePoints
      .map((point) => {
        return {
          time: point.timestamp, // Use raw timestamp, let XAxis formatter handle it
          fullTimestamp: point.timestamp,
          // Sessions data
          sessions: point.sessions.count,
          // TTS latency data
          ttsLatency: point.ttsLatency.avg,
          ttsLatencyBand: [point.ttsLatency.p50, point.ttsLatency.p95],
          // STT latency data
          sttLatency: point.sttLatency.avg,
          sttLatencyBand: [point.sttLatency.p50, point.sttLatency.p95],
          // Turn around time data
          turnAroundTime: point.turnAroundTime.avg,
          turnAroundBand: [point.turnAroundTime.p50, point.turnAroundTime.p95],
          // Session length data
          sessionLength: point.sessionLength.avg,
          sessionLengthBand: [point.sessionLength.p50, point.sessionLength.p95],
          sessionLengthP50: point.sessionLength.p50,
          sessionLengthP95: point.sessionLength.p95,
          // Error rate
          errorRate: point.errorRate,
          // Transfer rate
          transferRate: point.transferRate,
          // Turn depth data
          turnDepth: point.turnDepth?.avg || 0,
          turnDepthBand: [point.turnDepth?.p50 || 0, point.turnDepth?.p95 || 0],
        };
      })
      .sort((a, b) => new Date(a.fullTimestamp).getTime() - new Date(b.fullTimestamp).getTime());
  }, [currentAnalytics?.timePoints, currentAnalytics?.startDate, currentAnalytics?.endDate, selectedTimeframe]);

  // Organize metrics by categories
  const observabilityMetrics = currentAnalytics
    ? [
        { name: "TTS Latency", value: currentAnalytics.metrics.avgFirstTtsLatencyMs, format: formatMs, color: COLORS[0] },
        { name: "STT Latency", value: currentAnalytics.metrics.avgSttLatencyMs, format: formatMs, color: COLORS[1] },
        { name: "Turn Around Time", value: currentAnalytics.metrics.avgTurnAroundTimeMs, format: formatMs, color: COLORS[2] },
      ]
    : [];

  const sessionMetrics = currentAnalytics
    ? [
        { name: "Session Length", value: currentAnalytics.metrics.avgSessionLengthMinutes, format: formatMinutes, color: COLORS[5] },
        { name: "Transfer Rate", value: currentAnalytics.metrics.transferRate, format: formatPercentage, color: COLORS[6] },
        { name: "Turn Depth", value: currentAnalytics.metrics.avgTurnDepth, format: (value: number) => value?.toFixed(1) || "0.0", color: COLORS[7] },
      ]
    : [];

  if (isLoading) {
    return (
      <View className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </View>
    );
  }

  if (error) {
    return (
      <Card className="mb-6">
        <View className="p-4 text-center">
          <Icon
            name="info"
            className="text-sm text-red-600 mr-1"
          />
          <Text className="text-sm font-semibold text-red-600 mr-1">Error Loading Analytics</Text>
          <Text className="text-xs text-gray-600 dark:text-gray-400">{error}</Text>
        </View>
      </Card>
    );
  }

  return (
    <View className="space-y-6">
      {/* Time Range Controls */}
      <Card className="p-4">
        <View className="space-y-4">
          <View className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 lg:gap-6">
            <View className="flex flex-col sm:flex-row sm:items-end gap-4">
              <View className="flex flex-col gap-1">
                <Text className="text-xs font-medium text-gray-600 dark:text-gray-400">Time Period</Text>
                <DropdownField
                  value={selectedTimeframe}
                  onChange={(value) => handleTimeframeChange(value as AnalyticsTimeframe)}
                  placeholder="Select time period"
                  options={timeframeOptions.map(({ key, label }) => ({
                    label,
                    value: key,
                  }))}
                  className="w-32"
                />
              </View>

              <View className="flex flex-col gap-1">
                <Text className="text-xs font-medium text-gray-600 dark:text-gray-400">P50-P95 Bands</Text>
                <Switch
                  options={[
                    { label: "Off", value: "off" },
                    { label: "On", value: "on" },
                  ]}
                  value={showPercentileBands ? "on" : "off"}
                  onChange={(value) => setShowPercentileBands(value === "on")}
                />
              </View>
            </View>

            <View className="flex flex-col sm:flex-row sm:items-end gap-3 lg:text-right">
              <DateInput
                label="From:"
                value={pendingStartDate}
                onChange={(e) => setPendingStartDate(e.target.value)}
              />

              <DateInput
                label="To:"
                value={pendingEndDate}
                onChange={(e) => setPendingEndDate(e.target.value)}
              />

              <Button
                onClick={handleCustomDateRange}
                variant="brand"
                isDisabled={!pendingStartDate || !pendingEndDate || isLoading}
                className="text-xs px-3 py-1 flex items-center gap-1 h-9"
              >
                <Icon
                  name="search"
                  size={12}
                />
                Apply
              </Button>
            </View>
          </View>
        </View>
      </Card>
      {/* Compact KPI Strip */}
      {currentAnalytics && (
        <View className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Card className="p-3">
            <View className="flex items-center gap-2">
              <Icon
                name="phone"
                size={14}
                className="text-blue-500"
              />
              <View>
                <Text className="text-xs text-gray-500 dark:text-gray-400 mr-1">Sessions</Text>
                <Text className="text-sm font-bold text-gray-900 dark:text-white">{currentAnalytics.totalSessions.toLocaleString()}</Text>
              </View>
            </View>
          </Card>

          <Card className="p-3">
            <View className="flex items-center gap-2">
              <Icon
                name="clock"
                size={14}
                className="text-green-500"
              />
              <View>
                <Text className="text-xs text-gray-500 dark:text-gray-400 mr-1">Avg Length</Text>
                <Text className="text-sm font-bold text-gray-900 dark:text-white">{formatMinutes(currentAnalytics.metrics.avgSessionLengthMinutes)}</Text>
              </View>
            </View>
          </Card>
        </View>
      )}

      {currentAnalytics && (
        <>
          {/* Unified Dashboard */}
          <View className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Main Chart Area - Left Side */}
            <View className="lg:col-span-8">
              <Card>
                <View className="p-4">
                  {/* Chart Tabs */}
                  <View className="flex items-center gap-1 mb-4 border-b border-gray-200 dark:border-gray-700">
                    <Button
                      variant={activeTab === "sessions" ? "brand" : "ghost"}
                      onClick={() => setActiveTab("sessions")}
                      className="text-xs px-3 py-1.5 rounded-t-lg rounded-b-none"
                    >
                      Sessions
                    </Button>
                    <Button
                      variant={activeTab === "observability" ? "brand" : "ghost"}
                      onClick={() => setActiveTab("observability")}
                      className="text-xs px-3 py-1.5 rounded-t-lg rounded-b-none"
                    >
                      Observability
                    </Button>
                    <Button
                      variant={activeTab === "user_experience" ? "brand" : "ghost"}
                      onClick={() => setActiveTab("user_experience")}
                      className="text-xs px-3 py-1.5 rounded-t-lg rounded-b-none"
                    >
                      User Experience
                    </Button>
                  </View>

                  {/* Chart Content */}
                  <View style={{ height: 240 }}>
                    {activeTab === "sessions" && (
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="time"
                            tick={{ fontSize: 10 }}
                            interval="preserveStartEnd"
                            tickFormatter={timeAxisFormatter}
                          />
                          <YAxis tick={{ fontSize: 10 }} />
                          <Tooltip
                            content={
                              <CustomTooltip
                                timeframe={selectedTimeframe}
                                timezone={displayTimezone}
                              />
                            }
                          />
                          <Line
                            type="monotone"
                            dataKey="sessions"
                            stroke={COLORS[6]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                            name="Sessions"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                    {activeTab === "observability" && (
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="time"
                            tick={{ fontSize: 10 }}
                            interval="preserveStartEnd"
                            tickFormatter={timeAxisFormatter}
                          />
                          <YAxis tick={{ fontSize: 10 }} />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 10 }}
                          />
                          <Tooltip
                            content={
                              <CustomTooltip
                                timeframe={selectedTimeframe}
                                timezone={displayTimezone}
                              />
                            }
                          />
                          {showPercentileBands && (
                            <>
                              <Area
                                type="monotone"
                                dataKey="ttsLatencyBand"
                                stroke="none"
                                fill={COLORS[0]}
                                fillOpacity={0.2}
                                connectNulls
                                dot={false}
                                activeDot={false}
                                name="P50-P95 TTS Range"
                              />
                              <Area
                                type="monotone"
                                dataKey="sttLatencyBand"
                                stroke="none"
                                fill={COLORS[1]}
                                fillOpacity={0.2}
                                connectNulls
                                dot={false}
                                activeDot={false}
                                name="P50-P95 STT Range"
                              />
                              <Area
                                type="monotone"
                                dataKey="turnAroundBand"
                                stroke="none"
                                fill={COLORS[3]}
                                fillOpacity={0.2}
                                connectNulls
                                dot={false}
                                activeDot={false}
                                name="P50-P95 TurnAround Range"
                              />
                            </>
                          )}
                          <Line
                            type="monotone"
                            dataKey="ttsLatency"
                            stroke={COLORS[0]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Avg First TTS Latency (ms)"
                            connectNulls={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="sttLatency"
                            stroke={COLORS[1]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Avg STT Latency (ms)"
                            connectNulls={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="turnAroundTime"
                            stroke={COLORS[3]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Avg Turn Around Time (ms)"
                            connectNulls={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                    {activeTab === "user_experience" && (
                      <ResponsiveContainer
                        width="100%"
                        height="100%"
                      >
                        <ComposedChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="time"
                            tick={{ fontSize: 10 }}
                            interval="preserveStartEnd"
                            tickFormatter={timeAxisFormatter}
                          />
                          <YAxis tick={{ fontSize: 10 }} />
                          <YAxis
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 10 }}
                          />
                          <Tooltip
                            content={
                              <CustomTooltip
                                timeframe={selectedTimeframe}
                                timezone={displayTimezone}
                              />
                            }
                          />
                          {showPercentileBands && (
                            <>
                              <Area
                                type="monotone"
                                dataKey="sessionLengthBand"
                                stroke="none"
                                fill={COLORS[5]}
                                fillOpacity={0.3}
                                connectNulls
                                dot={false}
                                activeDot={false}
                                name="P50-P95 Session Range"
                              />
                              <Area
                                type="monotone"
                                dataKey="turnDepthBand"
                                stroke="none"
                                fill={COLORS[7]}
                                fillOpacity={0.3}
                                connectNulls
                                dot={false}
                                activeDot={false}
                                name="P50-P95 Turn Depth Range"
                              />
                            </>
                          )}
                          <Line
                            type="monotone"
                            dataKey="sessionLength"
                            stroke={COLORS[5]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Avg Session Length (minutes)"
                            connectNulls={false}
                          />
                          <Line
                            type="monotone"
                            dataKey="transferRate"
                            stroke={COLORS[6]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Transfer Rate (%)"
                            connectNulls={false}
                            yAxisId="right"
                          />
                          <Line
                            type="monotone"
                            dataKey="turnDepth"
                            stroke={COLORS[7]}
                            strokeWidth={2}
                            dot={{ r: 2 }}
                            name="Avg Turn Depth"
                            connectNulls={false}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    )}
                  </View>
                </View>
              </Card>
            </View>

            {/* Live Metrics Summary - Right Side */}
            <View className="lg:col-span-4 space-y-3">
              <Card className="p-4">
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Observability Metrics</Text>
                <View className="space-y-2">
                  {observabilityMetrics.slice(0, 5).map((metric, index) => (
                    <View
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <View className="flex items-center gap-1">
                        <Text className="text-xs text-gray-600 dark:text-gray-400">{metric.name}</Text>
                        {metric.name === "TTS Latency" && (
                          <UITooltip content="Measures TTS service responsiveness for each response - the time from when text is sent to the TTS service until the first audio frame is received back.">
                            <Icon
                              name="info"
                              className="text-gray-400 !text-[12px] !w-[12px] !h-[12px]"
                            />
                          </UITooltip>
                        )}
                        {metric.name === "STT Latency" && (
                          <UITooltip content="Measures STT processing latency - the time from when the user stops talking until the first transcription is received.">
                            <Icon
                              name="info"
                              className="text-gray-400 !text-[12px] !w-[12px] !h-[12px]"
                            />
                          </UITooltip>
                        )}
                        {metric.name === "Turn Around Time" && (
                          <UITooltip content="Measures end-to-end response latency - the time from when a user stops speaking until audio starts being sent back to them.">
                            <Icon
                              name="info"
                              className="text-gray-400 !text-[12px] !w-[12px] !h-[12px]"
                            />
                          </UITooltip>
                        )}
                      </View>
                      <Text className="text-xs font-semibold text-gray-900 dark:text-white">{metric.format(metric.value)}</Text>
                    </View>
                  ))}
                </View>
              </Card>

              <Card className="p-4">
                <Text className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">User Experience</Text>
                <View className="space-y-2">
                  {sessionMetrics.map((metric, index) => (
                    <View
                      key={index}
                      className="flex justify-between items-center"
                    >
                      <View className="flex items-center space-x-1">
                        <Text className="text-xs text-gray-600 dark:text-gray-400">{metric.name}</Text>
                        {metric.name === "Session Length" && (
                          <UITooltip content="Average duration of voice sessions - the time from when a session starts until it ends.">
                            <Icon
                              name="info"
                              className="text-gray-400 !text-[12px] !w-[12px] !h-[12px]"
                            />
                          </UITooltip>
                        )}
                        {metric.name === "Transfer Rate" && (
                          <UITooltip content="Percentage of sessions that were transferred to a human agent or another service.">
                            <Icon
                              name="info"
                              className="text-gray-400 !text-[12px] !w-[12px] !h-[12px]"
                            />
                          </UITooltip>
                        )}
                        {metric.name === "Turn Depth" && (
                          <UITooltip content="Average number of conversation turns per session - measures how many back-and-forth exchanges occurred.">
                            <Icon
                              name="info"
                              className="text-gray-400 !text-[12px] !w-[12px] !h-[12px]"
                            />
                          </UITooltip>
                        )}
                      </View>
                      <Text className="text-xs font-semibold text-gray-900 dark:text-white">{metric.format(metric.value)}</Text>
                    </View>
                  ))}
                </View>
              </Card>
            </View>
          </View>

          {/* Time Period Info */}
          <View className="text-center">
            <Text className="text-xs text-gray-500 dark:text-gray-400">
              {selectedTimeframe === "custom" && startDate && endDate
                ? `Data from ${startDate.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1")} to ${endDate.replace(/(\d{4})-(\d{2})-(\d{2})/, "$2/$3/$1")} (custom range)`
                : `Data from ${new Date(currentAnalytics.startDate).toLocaleString()} to ${new Date(currentAnalytics.endDate).toLocaleString()} (${timeframeOptions.find((opt) => opt.key === selectedTimeframe)?.label || selectedTimeframe})`}
            </Text>
          </View>
        </>
      )}
    </View>
  );
};
